import * as beaver from 'beaver-logger';
import * as aiqConfig from 'config';
import { store } from 'store/store';
import { appendLogEvents, isLogOutboxAvailable, PERSIST_INTERVAL_MS, setLogOutboxDiagnostics, settlePendingAuthor } from 'services/logging/logOutbox';
import {
    createMemoryLogSender,
    PENDING_AUTHOR_MAX_AGE_MS,
    SENDER_ROLE,
    startLogOutboxSender,
    triggerLogOutboxSend,
} from 'services/logging/logOutboxSender';
import { PendingAuthorKeys } from 'utils/logging/pendingAuthorKeys';

const logsUrl = `${aiqConfig.GENERAL.ADDRESSES.HTTP.MAIN}/logs`;

const aiqLoggerConfig = {
    url: logsUrl,
    logLevel: -1,
    // beaver only hands the logs to the outbox transport (IndexedDB) every few seconds; sending
    // to the server is done by the outbox sender, so a reload, a closed tab or a network outage loses at
    // most the last few seconds instead of up to a minute.
    flushInterval: PERSIST_INTERVAL_MS,
    enableSendBeacon: false,
};

class AIQLogger {
    #logger;
    #windowUid;
    #memorySender = createMemoryLogSender({ url: logsUrl });
    #originalConsole = { log: console.log, warn: console.warn, error: console.error };
    // Author stored with every log record. Until the user is known (/auth, a session handed over by another tab)
    // the records are written right away but marked as waiting for their author, and get it once it is known.
    #username = null;
    #authorSettled = false;
    // Keys of the records this tab wrote while waiting for the author.
    #pendingKeys = new PendingAuthorKeys({ maxAgeMs: PENDING_AUTHOR_MAX_AGE_MS });

    constructor(config) {
        this.#logger = beaver.Logger({ ...config, transport: this.#outboxTransport });
        this.#setTracking();
        this.#registerEventListeners();
        this.#wireWindowConsole();
        setLogOutboxDiagnostics(this.#originalConsole.warn);
        startLogOutboxSender({ url: logsUrl, globalScope: window, role: SENDER_ROLE.TAB });
    }

    /**
     *
     * @param {object} content
     * @param {string} event any string is acceptable, default='log'
     * @param {string} level posible options [debug, info, warn, error]  ||  default='info'
     */
    log(content, event = 'log', level = 'info') {
        this.#logger[level](event, content);
    }

    /**
     * Hands everything buffered so far to the outbox (a local write, fast).
     * @returns {Promise<void>}
     */
    flush() {
        return Promise.resolve(this.#logger.immediateFlush());
    }

    // Beaver transport: persist into the IndexedDB outbox instead of sending. Without IndexedDB (private
    // mode, quota) the logs go to a capped in-memory buffer that is sent once a minute.
    #outboxTransport = async ({ json }) => {
        const source = json?.tracking?.[0]?.uid ?? null;
        const events = json?.events ?? [];
        if (!events.length) return;
        // Taken when beaver hands the batch over: a user set while it is being written applies to later logs.
        const authorPending = !this.#authorSettled;
        const username = this.#username;

        if (await isLogOutboxAvailable()) {
            try {
                const keys = await appendLogEvents({ source, username, events, authorPending });
                if (authorPending) this.#onPendingWritten(keys);
                return;
            } catch (err) {
                this.#originalConsole.warn(`[AIQLogger] Persisting logs failed, keeping them in memory: ${err?.message}`);
            }
        }
        // The in-memory fallback cannot wait for the author: a log written before the user is known goes out under
        // the user of this tab's session, as the fallback sends from this tab right away.
        this.#memorySender.push(source, username, events);
        await this.#memorySender.flush();
    };

    #onPendingWritten(keys) {
        this.#pendingKeys.add(keys);
        // The author got known while this write was in progress: settle these records too, or they would wait
        // until the sender gives up on them.
        if (this.#authorSettled) this.#settlePending();
    }

    #settlePending() {
        const username = this.#username;
        const keys = this.#pendingKeys.take();
        if (!keys.length) return;
        settlePendingAuthor({ keys, username }).catch((err) =>
            this.#originalConsole.warn(`[AIQLogger] Setting the author of waiting logs failed: ${err?.message}`)
        );
    }

    // One function for add and remove: a fresh .bind() each time made removeEventListeners remove nothing.
    #onError = (error) => this.#logError(error);

    #logError(error) {
        this.log(
            {
                message: error.message || error?.reason?.message || error.reason,
                type: error.type,
                code: error.code,
            },
            error.type,
            'error'
        );
    }

    #setTracking() {
        this.#logger.addTrackingBuilder(() => ({ uid: this.#getWindowUid() }));
        this.#logger.addMetaBuilder(() => this.#logger.track());
    }

    #getWindowUid() {
        if (this.#windowUid) return this.#windowUid;
        return (this.#windowUid = store?.getState?.()?.transactions?.sharedWorker?.uid);
    }

    #wireWindowConsole() {
        const { log: consoleLog, error: consoleError, warn: consoleWarn } = this.#originalConsole;

        const newLog = function ({ logToConsole, logEvent, level }, ...contentToLog) {
            logToConsole(...contentToLog);
            this.log(contentToLog, logEvent, level);
        };

        console.log = newLog.bind(this, {
            logToConsole: consoleLog,
            logEvent: 'console.log',
            level: 'info',
        });

        console.error = newLog.bind(this, {
            logToConsole: consoleError,
            logEvent: 'console.error',
            level: 'warn',
        });

        console.warn = newLog.bind(this, {
            logToConsole: consoleWarn,
            logEvent: 'console.warn',
            level: 'warn',
        });
    }

    // A hidden tab gets its timers throttled (down to once a minute), so persist right when it is hidden instead
    // of relying on the interval; a hidden tab that is then closed loses at most what it logged after that
    // (beaver also persists on pagehide, but a write started while the page unloads may not complete).
    #persistWhenHidden = () => {
        if (document.visibilityState === 'hidden') this.#logger.immediateFlush();
    };

    #registerEventListeners() {
        window.addEventListener('error', this.#onError);
        window.addEventListener('unhandledrejection', this.#onError);
        document.addEventListener('visibilitychange', this.#persistWhenHidden);
    }

    removeEventListeners() {
        window.removeEventListener('error', this.#onError);
        window.removeEventListener('unhandledrejection', this.#onError);
        document.removeEventListener('visibilitychange', this.#persistWhenHidden);
    }

    /**
     * Sets the author of the logs: the ones still waiting for it and every later one. An empty value (logout,
     * setUser(null)) deliberately changes nothing: the logs a tab writes after a logout belong to the user who
     * logged out, until the next user is set.
     * @param {string|null|undefined} username
     */
    setUsername(username) {
        if (!username) return;
        // The author was already settled (a previous user, or unknown after /auth named none): what beaver has
        // buffered was logged under that author. The transport takes the author synchronously, so this persists it
        // under that one before the name changes below.
        if (this.#authorSettled && username !== this.#username) this.#logger.immediateFlush();
        this.#username = username;
        this.#authorSettled = true;
        this.#settlePending();
    }

    /**
     * /auth did not name a user (no session, or it failed): the logs waiting for an author stay without one and are
     * sent right away instead of waiting. A user set later applies only to the logs written after it.
     */
    settleUnknownAuthor() {
        if (this.#authorSettled) return;
        this.#authorSettled = true;
        this.#settlePending();
    }

    /**
     * Before logout: persists what is buffered (still under the logged-in user) and sends it right away, while the
     * session is still valid. The same beaver logger is kept: a new one would lose the tracking (log_source) and
     * leave the old one's interval and unload listeners running, as beaver cannot be stopped.
     * @returns {Promise<void>} Settles when the send attempt is done; never rejects
     */
    flushBeforeLogout() {
        return Promise.resolve(this.#logger.immediateFlush())
            .then(() => triggerLogOutboxSend({ jitterMs: 0, deduplicate: false }))
            .catch(() => {});
    }

    // The tracking builder from #setTracking reads this field, so a new registration (new uid) needs no new builder.
    setWindowUid(uid) {
        if (!uid) return;
        this.#windowUid = uid;
    }
}

export const aiqLogger = new AIQLogger(aiqLoggerConfig);
